<script setup lang="ts">
import { QuillEditor } from '@vueup/vue-quill'
import { useField, useForm } from 'vee-validate'
import { computed, ref, watch } from 'vue'
import * as yup from 'yup'
import type { OrderType } from '@/api/functions/services/get-order-types.action'
import type { ServiceCategoryOption } from '@/api/functions/services/get-service-categories.action'
import type { ServiceTypeOption } from '@/api/functions/services/get-service-types.action'
import type { ServiceProviderOption } from '@/api/functions/services/providers/get-service-providers.action'
import { type UpdateServicePayload, updateServiceAction } from '@/api/functions/services/update-service.action.ts'
import { uploadServiceLogoAction } from '@/api/functions/services/upload-service-logo.action'
import type { Service } from '@/api/typings/service.typing'
import { useMutationApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'
import '@vueup/vue-quill/dist/vue-quill.snow.css'

type ServiceStatus = 'ACTIVE' | 'INACTIVE'

interface ServiceFormValues {
  name: string
  slug: string
  providerName: string
  category: string[]
  serviceType: string
  shortDescription: string
  description: string
  status: ServiceStatus
  isFeatured: boolean
  featuredSortOrder: number | null
  orderTypes: string[]
  sortOrder: number | null
}

const props = defineProps<{
  modelValue: boolean
  serviceToEdit: Service | null
  categories: ServiceCategoryOption[]
  serviceTypeOptions: ServiceTypeOption[]
  orderTypeOptions: OrderType[]
  providerOptions: ServiceProviderOption[]
}>()

const emit = defineEmits<{
  (event: 'update:modelValue', value: boolean): void
  (event: 'confirm'): void
  (event: 'cancel'): void
}>()

const toolbarOptions = [
  [{ header: [1, 2, 3, 4, 5, 6, false] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ align: [] }],
  ['blockquote', 'code-block'],
  ['link'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ direction: 'rtl' }],
  [{ color: [] }, { background: [] }],
  ['clean'],
]

const toast = useToast()

const serviceLogo = ref<File | null>(null)

const schema = yup.object({
  name: yup
    .string()
    .required('نام سرویس الزامی است')
    .min(2, 'نام سرویس باید حداقل 2 کاراکتر باشد')
    .max(100, 'نام سرویس نمی‌تواند بیشتر از 100 کاراکتر باشد'),

  slug: yup.string().required('اسلاگ الزامی است'),

  providerName: yup.string().required('نام ارائه‌دهنده الزامی است'),

  category: yup.array().of(yup.string()).min(1, 'حداقل یک دسته‌بندی را انتخاب کنید').required('دسته‌بندی الزامی است'),

  serviceType: yup.string().required('نوع سرویس الزامی است'),

  orderTypes: yup.array().of(yup.string()).min(1, 'حداقل یک نوع سفارش را انتخاب کنید').required('نوع سفارش الزامی است'),

  shortDescription: yup.string().max(500, 'توضیحات کوتاه نمی‌تواند بیشتر از 500 کاراکتر باشد'),

  description: yup.string().max(2000, 'توضیحات نمی‌تواند بیشتر از 2000 کاراکتر باشد'),

  status: yup.string().required('وضعیت الزامی است'),

  isFeatured: yup.boolean(),

  featuredSortOrder: yup
    .number()
    .nullable()
    .transform((value: number | null, originalValue: unknown) =>
      originalValue === '' || originalValue == null ? null : value
    )
    .typeError('ترتیب نمایش سرویس ویژه باید عدد باشد')
    .min(0, 'ترتیب نمایش نمی‌تواند منفی باشد')
    .when('isFeatured', {
      is: true,
      then: (field) => field.required('ترتیب نمایش سرویس ویژه الزامی است'),
    }),

  sortOrder: yup
    .number()
    .nullable()
    .transform((value: number | null, originalValue: unknown) =>
      originalValue === '' || originalValue == null ? null : value
    )
    .typeError('ترتیب نمایش باید عدد باشد')
    .min(0, 'ترتیب نمایش نمی‌تواند منفی باشد'),
})

const { handleSubmit, errors, resetForm } = useForm<ServiceFormValues>({
  validationSchema: schema,
  initialValues: {
    name: '',
    slug: '',
    providerName: '',
    category: [],
    serviceType: '',
    orderTypes: [],
    shortDescription: '',
    description: '',
    status: 'ACTIVE',
    isFeatured: true,
    featuredSortOrder: 0,
    sortOrder: 0,
  },
})

const { value: name } = useField<string>('name')
const { value: slug } = useField<string>('slug')
const { value: providerName } = useField<string>('providerName')
const { value: category } = useField<string[]>('category')
const { value: serviceType } = useField<string>('serviceType')
const { value: shortDescription } = useField<string>('shortDescription')
const { value: orderTypes } = useField<string[]>('orderTypes')
const { value: description } = useField<string>('description')
const { value: status } = useField<ServiceStatus>('status')
const { value: isFeatured } = useField<boolean>('isFeatured')
const { value: featuredSortOrder } = useField<number | null>('featuredSortOrder')
const { value: sortOrder } = useField<number | null>('sortOrder')

const statusOptions = [
  {
    label: 'فعال',
    value: 'ACTIVE',
  },
  {
    label: 'غیرفعال',
    value: 'INACTIVE',
  },
]

const currentLogo = computed(() => {
  return props.serviceToEdit?.logo_url || ''
})

const getOptionValue = (value: unknown): string => {
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value)
  }

  if (value && typeof value === 'object') {
    const option = value as Record<string, unknown>
    const result = option.slug ?? option.value ?? option.id

    return result == null ? '' : String(result)
  }

  return ''
}

const toArray = (value: unknown): unknown[] => {
  if (Array.isArray(value)) return value
  if (value == null || value === '') return []

  return [value]
}

const normalizeOptions = (value: unknown): string[] => {
  return toArray(value).map(getOptionValue).filter(Boolean)
}

const normalizeStatus = (service: Service): ServiceStatus => {
  return service.is_active ? 'ACTIVE' : 'INACTIVE'
}

const handleSlugInput = (event: Event) => {
  const input = event.target as HTMLInputElement

  slug.value = input.value
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z_]/g, '')
}

watch(
  () => [props.modelValue, props.serviceToEdit, props.categories] as const,
  ([isOpen, service]) => {
    if (!isOpen || !service) return

    const selectedCategories = (Array.isArray(service.categories) ? service.categories : [])
      .map((category) => props.categories.find((item) => item.slug === getOptionValue(category)))
      .filter((item): item is ServiceCategoryOption => Boolean(item))

    const serviceTypeSlug = getOptionValue(service.service_type)

    const serviceToEditType = props.serviceTypeOptions.find((item) => item.slug === serviceTypeSlug)

    const selectedOrderTypes = normalizeOptions(service.order_types)

    const selectedProvider = props.providerOptions.find((item) => item.slug === getOptionValue(service.provider))

    resetForm({
      values: {
        name: service.name ?? '',
        slug: service.slug ?? '',
        providerName: selectedProvider?.value ?? '',
        category: selectedCategories.map((item) => item.value),
        serviceType: serviceToEditType?.value ?? '',
        orderTypes: selectedOrderTypes.map((orderType) => {
          const option = props.orderTypeOptions.find((item) => item.id === orderType || item.slug === orderType)

          return option?.id ?? orderType
        }),
        shortDescription: service.short_description ?? '',
        description: service.description ?? '',
        status: normalizeStatus(service),
        isFeatured: service.is_featured ?? false,
        featuredSortOrder: service.popular_sort_order == null ? 0 : Number(service.popular_sort_order),
        sortOrder: service.sort_order == null ? 0 : Number(service.sort_order),
      },
    })
  },
  { immediate: true }
)

const updateServiceMutation = useMutationApi({
  mutationFn: (payload: UpdateServicePayload) => updateServiceAction(props.serviceToEdit!.slug!, payload),

  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const isSubmitting = computed(() => updateServiceMutation.isPending.value)

const onSubmit = handleSubmit(async (values: ServiceFormValues) => {
  if (!props.serviceToEdit?.slug) return

  const payload: UpdateServicePayload = {
    category_ids: values.category,
    provider_id: values.providerName,
    name: values.name,
    slug: values.slug,
    service_type_id: values.serviceType,
    short_description: values.shortDescription,
    description: values.description,
    is_featured: values.isFeatured,
    is_active: values.status === 'ACTIVE',
    sort_order: Number(values.sortOrder ?? 0),
    logo_url: serviceLogoUrl.value,
    popular_sort_order: values.isFeatured ? Number(values.featuredSortOrder ?? 0) : null,
    // plans: (props.serviceToEdit.plans ?? []).map((plan) => ({
    //     name: plan.name,
    //     slug: plan.slug,
    //     description: plan.description,
    //     duration_days: plan.duration_days,
    //     is_popular: plan.is_popular,
    //     is_active: plan.is_active,
    //     sort_order: plan.sort_order,

    //     prices: plan.prices.map((price) => ({
    //         region_id: price.region.id,
    //         currency_id: price.currency.id,
    //         pricing_type: price.pricing_type,
    //         amount: price.amount,
    //         unit: price.unit,
    //         billing_period: price.billing_period,
    //         min_quantity: price.min_quantity,
    //         max_quantity: price.max_quantity,
    //         is_starting_from: price.is_starting_from,
    //         is_official: price.is_official,
    //     })),
    // })),

    order_type_ids: values.orderTypes,
  }

  try {
    await updateServiceMutation.mutateAsync(payload)

    toast.success('سرویس با موفقیت ویرایش شد')
    emit('confirm')
    closeModal()
  } catch {}
})

const checkLogoUpload = () => {
  if (!props.serviceToEdit?.slug) {
    toast.error('برای آپلود لوگو ابتدا اسلاگ سرویس را وارد کنید', 'خطا')

    return false
  }

  return true
}

const uploadLogoMutation = useMutationApi({
  mutationFn: ({ serviceSlug, logo }: { serviceSlug: string; logo: File }) =>
    uploadServiceLogoAction(serviceSlug, logo),

  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const serviceLogoUrl = ref<string | null>(null)

const uploadServiceLogo = async (file: File) => {
  if (!props.serviceToEdit?.slug) return

  try {
    const response = await uploadLogoMutation.mutateAsync({
      serviceSlug: props.serviceToEdit.slug,
      logo: file,
    })

    serviceLogoUrl.value = response.data.logo_url

    toast.success('لوگوی سرویس با موفقیت آپلود شد')
  } catch {}
}

const closeModal = () => {
  emit('update:modelValue', false)
  emit('cancel')
  resetForm()
}
</script>

<template>
    <Modal :model-value="props.modelValue" @update:model-value="emit('update:modelValue', $event)" width="1000px"
        background="#FFF" :closable="true" title="ویرایش سرویس" closeDirection="left" class="update-request">
        <form @submit.prevent="onSubmit" class="flex flex-col gap-8">

            <!-- Basic Information -->
            <div class="w-full bg-white border border-[#d8d8d8] rounded-xl p-5 lg:p-8">
                <h3 class="text-[17px] text-[#1b325a] font-medium mb-7">
                    اطلاعات سرویس
                </h3>

                <div class="grid lg:grid-cols-2 gap-6">
                    <Input v-model="name" name="name" label="نام سرویس" type="text" :error="errors.name" />

                    <Input v-model="slug" name="slug" :disabled="true" label="اسلاگ" type="text"
                        @input="handleSlugInput" :error="errors.slug" />

                    <MultiSelect v-model="category" variant="checkbox" name="category" label="دسته‌بندی"
                        :options="categories" :error="errors.category" />

                    <Select v-model="serviceType" name="serviceType" label="نوع سرویس" :options="serviceTypeOptions"
                        :error="errors.serviceType" />

                    <MultiSelect v-model="orderTypes" variant="checkbox" name="orderTypes" label="نوع سفارش"
                        :options="orderTypeOptions" :error="errors.orderTypes" />

                    <Input v-model="sortOrder" name="sortOrder" label="ترتیب نمایش" type="number"
                        :error="errors.sortOrder" />

                    <div class="col-span-2">
                        <Input v-model="shortDescription" name="shortDescription" label="توضیحات کوتاه" type="text"
                            :error="errors.shortDescription" />
                    </div>

                    <div class="sm:col-span-2 min-w-0">
                        <label class="block text-sm text-[#1b325a] mb-3">
                            توضیحات
                        </label>

                        <div class="border border-[#d5ddea] rounded-xl">
                            <QuillEditor v-model:content="description" theme="snow" :toolbar="toolbarOptions"
                                content-type="html" placeholder="توضیحات سرویس را وارد کنید"
                                class="bg-white min-h-[100px] rounded-b-xl text-right" />
                        </div>


                        <p v-if="errors.description" class="text-xs text-[#ed145b] mt-2">
                            {{ errors.description }}
                        </p>
                    </div>

                </div>

                <div class="mt-14">
                    <FileUploader v-model="serviceLogo" label="لوگوی سرویس" :before-select="checkLogoUpload"
                        @change="uploadServiceLogo" />

                    <img v-if="currentLogo" :src="currentLogo" alt="لوگوی فعلی سرویس"
                        class="mt-3 h-16 w-16 rounded-lg object-contain" />
                </div>
            </div>

            <!-- Provider -->
            <div class="w-full bg-white border border-[#d8d8d8] rounded-xl p-5 lg:p-8">
                <h3 class="text-[17px] text-[#1b325a] font-medium mb-7">
                    ارائه‌دهنده سرویس
                </h3>

                <div class="grid lg:grid-cols-2 gap-6">
                    <Select v-model="providerName" name="providerName" label="ارائه‌دهنده" :options="providerOptions"
                        :error="errors.providerName" />
                </div>
            </div>

            <!-- Status -->
            <div class="w-full bg-white border border-[#d8d8d8] rounded-xl p-5 lg:p-8">
                <h3 class="text-[17px] text-[#1b325a] font-medium mb-7">
                    وضعیت سرویس
                </h3>

                <div class="grid lg:grid-cols-2 gap-6">
                    <Select v-model="status" name="status" label="وضعیت" :options="statusOptions"
                        :error="errors.status" />

                    <div class="flex items-center gap-4 px-4 h-12">
                        <div class="flex-1">
                            <Checkbox v-model="isFeatured" name="isFeatured" label="نمایش به عنوان سرویس ویژه"
                                color="#1b325a" labelColor="#1b325a" />
                        </div>

                        <div v-if="isFeatured" class="w-[240px] shrink-0">
                            <Input v-model="featuredSortOrder" class="h-12!" name="featuredSortOrder"
                                label="ترتیب نمایش در سرویس‌های ویژه" type="number" :error="errors.featuredSortOrder" />
                        </div>
                    </div>
                </div>
            </div>

            <!-- Actions -->
            <div class="flex justify-end gap-4 mt-2 mb-8">
                <Button type="button" variant="outline" label="بازگشت" :isAdmin="true"
                    class="px-8 h-12 rounded-xl border-2" @click="closeModal" :disabled="isSubmitting" />

                <Button type="submit" label="ویرایش سرویس" :isAdmin="true" class="px-10 h-12 rounded-xl"
                    :disabled="isSubmitting" :loading="isSubmitting" />
            </div>
        </form>
    </Modal>
</template>

<style scope>
.ql-toolbar.ql-snow {
    background-color: white;
    border-color: #eaeef5;
    border-top-right-radius: 10px;
    border-top-left-radius: 10px;
}

.ql-toolbar {
    direction: ltr !important;
    text-align: left !important;
}


.ql-editor {
    direction: rtl;
    text-align: right;
    font-family: inherit;
    min-height: 100px;
}

.ql-container.ql-snow {
    border: none
}
</style>